/**
 * Move chrome-tree `responsiveStyles` out of the React Router flight payload.
 *
 * Measured 2026-08-27 (FSA homepage, post-#998): the six layout chrome trees
 * carry 67.5KB of per-block `responsiveStyles` JSON — the single largest
 * remaining field class in the `routes/layout` flight payload, shipped on
 * EVERY route. The SDK reads those bags only to generate per-block CSS
 * (`<style href="bsdkb-…" precedence="default">`, hoisted and deduped by
 * React 19 — see the `@builder.io/sdk-react` patch from PR #985).
 *
 * This module generates that CSS server-side ONCE per cached entry, strips
 * the bags from the tree, and lets the layout render the resulting styles as
 * the same hoisted `<style>` resources. Because the TREE is stripped before
 * the loader returns, server and client render the identical (style-less)
 * block markup — there is no hydration asymmetry in the block DOM. The only
 * asymmetry is the sidecar `<style>` hoistables, which the server renders and
 * the client does not; React 19 hydrates hoistable style resources by
 * `href`-keyed adoption, not position, and never removes server-rendered
 * styles a client render doesn't claim (verified live — see PR body).
 *
 * CSS generation replicates the SDK's `BlockStyles` output byte-for-byte
 * (same rule template, indentation, part-joining and djb2-xor `href` hash),
 * pinned by `builder-ssr-styles.test.ts` against block fixtures captured from
 * the live payload whose hashes must appear in the live document head.
 *
 * Safety guards — a block is left untouched (bags kept, SDK renders it
 * exactly as today) when:
 *  - it carries `animations` (the SDK folds hover-animation CSS into the same
 *    style string; stripping would change that block's href), or
 *  - it carries `bindings` (dynamic expressions may rewrite styles at render
 *    time), or
 *  - the entry still has `meta.breakpoints` (custom breakpoints change the
 *    media queries; chrome entries are `slimBuilderEntry`-processed so `meta`
 *    is normally absent and the SDK uses the defaults replicated here).
 *
 * REVALIDATION PATH (PR #1042 blocker 5): the layout revalidates on every
 * successful account/auth POST (see layout.tsx `shouldRevalidate`), and
 * that response can carry a DIFFERENT chrome tree than the document did — a
 * scheduled alert-bar swap (15-min TTL, SFCC-11742) or a chrome publish. The
 * new tree arrives stripped, its CSS was never in this document's head, and
 * the original design shipped no styles on the client path at all — so a
 * footer publish degraded every styled chrome block for every ACTIVE session
 * until a hard reload (the original "cosmetic, self-healing" note under-
 * scoped this: it fires on routine mid-session revalidations, not only on a
 * publish racing a session). The fix: the loader ALSO returns the extracted
 * styles in loader data — but only for non-document (data/revalidation)
 * requests, so the document flight keeps the full Wave-3 diet — and the
 * layout renders whatever arrives as the same href-keyed precedence styles.
 * React 19 dedupes by href against the head, so pre-existing styles are
 * no-ops and NEW ones are inserted — restyled chrome, no reload needed.
 * Data responses grow by the CSS text (~15-20KB) on basket/account POSTs;
 * that is the accepted price of correct mid-session chrome.
 *
 * Client behavior: `extract` is only called by the layout LOADER (server).
 * The component-side `peek` only reads the WeakMap memo, so on the client it
 * returns nothing — the client renders styles from loader data instead (see
 * `dedupeChromeSsrStyles`, which merges the two sources).
 */

export type ChromeSsrStyle = { href: string; css: string };

const SDK_ELEMENT_TYPE = "@builder.io/sdk:Element";

/**
 * SDK default breakpoints (`ln` in the edge bundle): medium ends at 991px,
 * small at 640px. `xsmall` is only honored when `content.meta.breakpoints`
 * declares it — chrome entries have `meta` dropped by `slimBuilderEntry`, so
 * the SDK always uses these defaults for chrome (entries that still carry
 * `meta.breakpoints` are skipped entirely, see guard above).
 */
const MEDIA_MEDIUM = "@media (max-width: 991px)";
const MEDIA_SMALL = "@media (max-width: 640px)";

/** SDK `camelToKebabCase` — the exact regex from the edge bundle. */
const camelToKebab = (s: string): string =>
  s ? s.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase() : "";

/**
 * SDK `createCssClass` (`Ue`): one rule with the SDK's exact template —
 * 4-space body indent, 2-space closing indent, and the 6/4-space media wrap.
 * Non-string values are dropped (the SDK maps them to `undefined` and
 * filters), matching bags that mix strings with editor artifacts.
 */
function cssRule(
  className: string,
  styles: Record<string, unknown>,
  mediaQuery?: string,
): string {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(styles)) {
    if (typeof value === "string")
      lines.push(`${camelToKebab(key)}: ${value};`);
  }
  const rule = `.${className} {
    ${lines.join("\n")}
  }`;
  return mediaQuery
    ? `${mediaQuery} {
      ${rule}
    }`
    : rule;
}

/** SDK `InlinedStyles` href: djb2-xor over the style string, base36. */
export function styleHref(css: string): string {
  let h = 5381;
  for (let i = 0; i < css.length; i++) h = (h * 33) ^ css.charCodeAt(i);
  return `bsdkb-${(h >>> 0).toString(36)}-${css.length}`;
}

type StyleBag = Record<string, unknown>;
type ResponsiveStyles = {
  large?: StyleBag;
  medium?: StyleBag;
  small?: StyleBag;
  xsmall?: StyleBag;
};

/**
 * SDK `BlockStyles` string for a block WITHOUT animations, under default
 * breakpoints: `[large, medium, small, xsmall, hover].join(" ")` where the
 * xsmall part is always "" (no `meta.breakpoints`) and hover is "" (animated
 * blocks are never extracted). Empty parts still contribute their join
 * spaces — that padding is part of the hashed string, so it must stay.
 */
export function blockCss(id: string, rs: ResponsiveStyles): string {
  const large = rs.large ? cssRule(id, rs.large) : "";
  const medium = rs.medium ? cssRule(id, rs.medium, MEDIA_MEDIUM) : "";
  const small = rs.small ? cssRule(id, rs.small, MEDIA_SMALL) : "";
  return [large, medium, small, "", ""].join(" ");
}

const memo = new WeakMap<object, ChromeSsrStyle[]>();

const isNonEmptyRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && Object.keys(v).length > 0;

function extractFromBlocks(node: unknown, out: ChromeSsrStyle[]): void {
  if (Array.isArray(node)) {
    for (const item of node) extractFromBlocks(item, out);
    return;
  }
  if (!node || typeof node !== "object") return;
  const obj = node as Record<string, unknown>;

  if (
    obj["@type"] === SDK_ELEMENT_TYPE &&
    typeof obj.id === "string" &&
    obj.id &&
    isNonEmptyRecord(obj.responsiveStyles) &&
    // Hover-animation CSS folds into the same style string — leave animated
    // blocks to the SDK so their href stays intact.
    !(Array.isArray(obj.animations) && obj.animations.length > 0) &&
    // Bindings can rewrite styles at render time — leave dynamic blocks alone.
    !isNonEmptyRecord(obj.bindings)
  ) {
    const rs = obj.responsiveStyles as ResponsiveStyles;
    if (rs.large || rs.medium || rs.small) {
      const css = blockCss(obj.id, rs);
      out.push({ href: styleHref(css), css });
    }
    // Either way the bag is dead weight for the client: without breakpoint
    // bags (or with only an ignored `xsmall`) the SDK emits the same shared
    // whitespace-only style it already emits for every style-less block.
    delete obj.responsiveStyles;
  }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      extractFromBlocks(obj[key], out);
    }
  }
}

/**
 * LOADER-ONLY (server): extract + strip one entry's chrome styles, memoized
 * by entry identity so an in-memory-cached tree is only walked once.
 */
export function extractChromeSsrStyles(entry: unknown): ChromeSsrStyle[] {
  if (!entry || typeof entry !== "object") return [];
  const cached = memo.get(entry);
  if (cached) return cached;
  const obj = entry as { data?: { blocks?: unknown }; meta?: unknown };
  const styles: ChromeSsrStyle[] = [];
  // Custom breakpoints change the media queries — don't touch such entries.
  // (Chrome entries are slimBuilderEntry-processed, so `meta` is absent.)
  const meta = obj.meta as { breakpoints?: unknown } | undefined;
  if (!meta?.breakpoints && obj.data?.blocks) {
    extractFromBlocks(obj.data.blocks, styles);
  }
  memo.set(entry, styles);
  return styles;
}

/** Loader entrypoint: extract + strip every chrome tree, deduped by href. */
export function collectChromeSsrStyles(entries: unknown[]): ChromeSsrStyle[] {
  return dedupe(entries.flatMap(extractChromeSsrStyles));
}

/**
 * RENDER-SAFE: read-only view of what the loader extracted. On the client
 * the memo is empty (loaderData is deserialized into new objects and the
 * loader never runs there), so this returns [] and the caller renders null.
 */
export function peekChromeSsrStyles(entries: unknown[]): ChromeSsrStyle[] {
  return dedupe(
    entries.flatMap(e =>
      e && typeof e === "object" ? (memo.get(e) ?? []) : [],
    ),
  );
}

function dedupe(styles: ChromeSsrStyle[]): ChromeSsrStyle[] {
  const seen = new Set<string>();
  return styles.filter(s =>
    seen.has(s.href) ? false : (seen.add(s.href), true),
  );
}

/**
 * Merge the two style sources the layout renders from — the server-render
 * memo (`peek`, non-empty during SSR only) and the loader-data copy (present
 * on data/revalidation responses only) — deduped by href. Exactly one source
 * is non-empty per render in practice; the dedupe makes overlap harmless.
 */
export function dedupeChromeSsrStyles(
  ...sources: (ChromeSsrStyle[] | null | undefined)[]
): ChromeSsrStyle[] {
  return dedupe(sources.flatMap(s => s ?? []));
}
